import React, { FC } from 'react';
import type { ChartOptions, ChartType, Plugin } from 'chart.js';

/**
 * Release markers: dated go-live events served by the platform-stats API and
 * drawn as vertical dashed lines on every time-series chart on the Scaling
 * tab, so a metric can be read before vs after a change shipped.
 */

export interface ReleaseMarker {
  /** YYYY-MM-DD (UTC). */
  date: string;
  label: string;
}

export interface MarkerPosition {
  /** Fractional category index along the x axis. */
  index: number;
  label: string;
}

const utcMs = (isoDate: string) => Date.parse(`${isoDate}T00:00:00Z`);

/**
 * Map markers onto fractional positions along a category axis whose labels
 * are ISO dates (daily or weekly buckets). Markers before the first bucket or
 * after the last are dropped; anything inside is linearly interpolated.
 */
export function markerPositions(
  markers: readonly ReleaseMarker[],
  labelDates: readonly string[],
): MarkerPosition[] {
  if (markers.length === 0 || labelDates.length === 0) return [];
  const ticks = labelDates.map(utcMs);
  const first = ticks[0];
  const last = ticks[ticks.length - 1];

  return markers
    .map((m) => ({ ms: utcMs(m.date), label: m.label }))
    .filter((m) => !Number.isNaN(m.ms) && m.ms >= first && m.ms <= last)
    .sort((a, b) => a.ms - b.ms)
    .map(({ ms, label }) => {
      if (ms === last) return { index: ticks.length - 1, label };
      let i = 0;
      while (i < ticks.length - 2 && ticks[i + 1] <= ms) i++;
      const span = ticks[i + 1] - ticks[i];
      const frac = span > 0 ? (ms - ticks[i]) / span : 0;
      return { index: i + frac, label };
    });
}

const LINE_COLOUR = 'rgba(220, 38, 38, 0.75)';

export interface ReleaseMarkersPluginOptions {
  markers: readonly ReleaseMarker[];
  /** ISO dates of the x-axis categories, in axis order. */
  labelDates: readonly string[];
}

declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType> {
    releaseMarkers?: ReleaseMarkersPluginOptions;
  }
}

/**
 * Marker positions are resolved at draw time from `options.plugins.releaseMarkers`
 * rather than captured when the plugin is built: react-chartjs-2 only passes
 * `plugins` to the Chart constructor, whereas `options` are re-applied on every
 * change, so a range switch (30d → 90d) moves the marker with the new axis.
 */
export const releaseMarkersPlugin: Plugin<'line'> = {
  id: 'releaseMarkers',
  afterDatasetsDraw(chart, _args, opts) {
    const positions = markerPositions(
      opts?.markers ?? [],
      opts?.labelDates ?? [],
    );
    const xScale = chart.scales.x;
    if (!xScale || positions.length === 0) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = LINE_COLOUR;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    for (const p of positions) {
      const x = xScale.getPixelForValue(p.index);
      if (x < chartArea.left || x > chartArea.right) continue;
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    }
    ctx.restore();
  },
};

export const RELEASE_MARKER_PLUGINS = [releaseMarkersPlugin];

/** Attach marker data to a line chart's options for `releaseMarkersPlugin`. */
export function withReleaseMarkers(
  options: ChartOptions<'line'>,
  markers: readonly ReleaseMarker[],
  labelDates: readonly string[],
): ChartOptions<'line'> {
  return {
    ...options,
    plugins: {
      ...options.plugins,
      releaseMarkers: { markers, labelDates },
    },
  };
}

/**
 * Legend line under a chart naming each marker drawn on it, since the canvas
 * label alone is hard to read on a phone.
 */
export const ReleaseMarkerLegend: FC<{
  markers: readonly ReleaseMarker[];
  labelDates: readonly string[];
}> = ({ markers, labelDates }) => {
  const shown = markerPositions(markers, labelDates);
  if (shown.length === 0) return null;
  const byLabel = new Map(shown.map((p) => [p.label, p]));
  return (
    <ul className='scaling-marker-legend'>
      {markers
        .filter((m) => byLabel.has(m.label))
        .map((m) => (
          <li key={`${m.date}|${m.label}`}>
            <span className='scaling-marker-swatch' aria-hidden='true' />
            <span className='scaling-marker-label'>{m.label}</span>
            <span className='scaling-marker-date'>
              {new Date(utcMs(m.date)).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                timeZone: 'UTC',
              })}
            </span>
          </li>
        ))}
    </ul>
  );
};
